// JoinUs.jsx

import React, { useState } from 'react';
import './JoinUs.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faAt, 
  faPhone, 
  faComment, 
  faGlobe 
} from '@fortawesome/free-solid-svg-icons';
import { ref, push, set } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import PageHero from '../components/PageHero';
import logo from '../assets/logos/WHITE Logo Text PNG.png';

// Define the department data with Font Awesome icon classes
const departments = [
  { 
    name: "Pre-Production", 
    iconClass: "fa-solid fa-pen-to-square", // Drafting/Planning
    description: "Scripting, planning, and scheduling.",
  },
  { 
    name: "Production", 
    iconClass: "fa-solid fa-video", // Filming
    description: "On-set filming and technical execution.",
  },
  { 
    name: "Post-Production", 
    iconClass: "fa-solid fa-scissors", // Cutting/Editing
    description: "Editing, sound design, and VFX.",
  },
  { 
    name: "Cast & Crew", 
    iconClass: "fa-solid fa-users", // Group/Team
    description: "Acting, lighting, and general support.",
  },
];

// Department Card Component
const DepartmentCard = ({ department, onApply }) => (
  <div className="department-card">
    {/* Use <i> tag for Font Awesome icon */}
    <div className="icon-container">
        <i className={department.iconClass}></i>
    </div>
    <h3>{department.name}</h3>
    <p>{department.description}</p>
    <button 
      className="apply-button" 
      onClick={() => onApply(department.name)}
      aria-label={`Apply for ${department.name}`}
    >
      Apply Now
    </button>
  </div>
);

// Application Modal Component (with department dropdown)
const ApplicationModal = ({ department, onClose, onDepartmentChange, departments }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = (e) => {
    onDepartmentChange(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.email || !formData.portfolio) {
      toast.error("Please fill in all required fields (First Name, Email, Portfolio).");
      return;
    }

    setIsSubmitting(true);
    try {
      const fullName = formData.firstName + (formData.lastName ? ' ' + formData.lastName : '');
      
      console.log('📝 Submitting Join Us application...');
      console.log('📊 Application data:', { department, ...formData, name: fullName });
      
      // Verify database connection
      if (!managementDb) {
        throw new Error('Database not initialized. Please check Firebase configuration.');
      }
      
      console.log('🔍 Database URL:', managementDb.app.options.databaseURL);
      
      // Create a new application entry in Firebase
      const applicationsRef = ref(managementDb, 'join-us/applications');
      console.log('🔗 Applications ref created:', applicationsRef);
      console.log('🔗 Full path:', applicationsRef.toString());
      
      const newApplicationRef = push(applicationsRef);
      console.log('🆔 New application ref created:', newApplicationRef);
      console.log('🆔 New application key:', newApplicationRef.key);
      
      const applicationData = {
        department: department,
        firstName: formData.firstName,
        lastName: formData.lastName || '',
        fullName: fullName,
        email: formData.email,
        phone: formData.phone || '',
        portfolio: formData.portfolio,
        coverLetter: formData.coverLetter || '',
        timestamp: Date.now(),
        date: new Date().toISOString(),
        status: 'new' // new, reviewed, accepted, rejected
      };

      console.log('💾 Saving application data:', applicationData);
      await set(newApplicationRef, applicationData);
      
      console.log('✅ Application saved successfully');
      toast.success(`Thank you, ${fullName}! Your application for ${department} has been submitted.`);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        portfolio: '',
        coverLetter: '',
      });
      
      onClose();
    } catch (error) {
      console.error('❌ Error submitting application:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Show more specific error message
      let errorMessage = 'Failed to submit application. Please try again.';
      if (error.code === 'PERMISSION_DENIED') {
        errorMessage = 'Permission denied. Please check database rules in Firebase Console.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="joinus-form-modal-overlay" onClick={onClose}>
      <div className="joinus-form-modal-card" onClick={e => e.stopPropagation()}>
        <div className="joinus-form-modal-aside">
            <img src={logo} alt="The Video Company" />
            <p>Join our crew of talented storytellers and filmmakers.</p>
        </div>
        <div className="joinus-form-modal-form">
          <button className="close-button" onClick={onClose} aria-label="Close Application Form">×</button>
          <h2>Apply for {department}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="department">Department</label>
              <select
                id="department"
                value={department}
                onChange={handleDepartmentChange}
                className="form-select"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da', color: '#555' }}
              >
                {departments.map((dept) => (
                  <option key={dept.name} value={dept.name}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1, marginRight: '8px' }}>
                <label htmlFor="firstName">First Name *</label>
                <div className="input-with-icon">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="First name" />
                </div>
              </div>
              <div className="form-group" style={{ flex: 1, marginLeft: '8px' }}>
                <label htmlFor="lastName">Last Name</label>
                <div className="input-with-icon">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faAt} className="input-icon" />
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Enter your email address" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                <input type="tel" id="phone" name="phone" value={formData.phone || ''} onChange={handleChange} required pattern="[0-9]{10,15}" placeholder="Enter your phone number" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="portfolio">Portfolio/Website Link *</label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faGlobe} className="input-icon" />
                <input type="url" id="portfolio" name="portfolio" value={formData.portfolio} onChange={handleChange} placeholder="e.g., https://my-portfolio.com" required />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="coverLetter">Cover Letter/Experience</label>
              <div className="input-with-icon">
                <FontAwesomeIcon icon={faComment} className="input-icon textarea-icon" />
                <textarea id="coverLetter" name="coverLetter" value={formData.coverLetter} onChange={handleChange} rows="4" placeholder="Tell us why you'd be a great fit for the team..."></textarea>
              </div>
            </div>
            <button 
              type="submit" 
              className="submit-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ... (rest of the file is the same)


// Main JoinUs Component
const JoinUs = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(departments[0].name);

  const handleApply = (departmentName) => {
    setSelectedDepartment(departmentName);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleDepartmentChange = (deptName) => {
    setSelectedDepartment(deptName);
  };

  return (
    <>
      <PageHero
        title="Join our film production crew"
        subtitle="Pick a department, share your work, and we'll reach out when a match appears."
        align="left"
      />
      <div className="joinus-portal">
      {/* Department Showcase (Single Row) */}
      <section className="department-showcase">
        {departments.map((dept) => (
          <DepartmentCard 
            key={dept.name} 
            department={dept} 
            onApply={handleApply} 
          />
        ))}
      </section>

      {/* Special Promotional Text */}
      <section className="promo-section">
        <p>
          "We are building a community of passionate storytellers. Join our crew and help us create the next cinematic masterpiece. Your talent makes the magic happen."
        </p>
      </section>

      {/* Interactive Application Flow - Modal */}
      {modalOpen && (
        <ApplicationModal 
          department={selectedDepartment} 
          onClose={handleCloseModal} 
          onDepartmentChange={handleDepartmentChange}
          departments={departments}
        />
      )}
    </div>
    </>
  );
};

export default JoinUs;